import { NavLink, useLocation } from "react-router-dom";

import {
  CalendarOutlined,
  DollarOutlined,
  FileTextFilled,
  HomeOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Menu } from "antd";

import type { MenuProps } from "antd";

type MenuItem = Required<MenuProps>["items"][number];

import { getUser } from "../../../services/auth";

const SidebarMenu = () => {
  const location = useLocation();
  const user = getUser();
  const userRole = user.role;

  // Function to get the selected key based on current path
  const getSelectedKey = (pathname: string) => {
    if (pathname.includes("/dashboard")) return "1";
    if (pathname.includes("/users")) return "2";
    if (pathname.includes("/files")) return "3";
    if (pathname.includes("/leaves")) return "4.1";
    if (pathname.includes("/claims")) return "4.2";
    if (pathname.includes("/payslips")) return "4.3";
    return "1"; // default to dashboard if no match
  };

  // Define allowed roles for each menu item
  const menuPermissions = {
    dashboard: ["admin", "branchAdmin", "lawyer", "staff", "client", "agent"],
    users: ["admin"],
    files: ["admin", "branchAdmin", "lawyer", "staff", "client", "agent"],
    staff: ["admin", "branchAdmin", "lawyer", "staff"],
  };
  // Function to check if user has permission for a specific menu item
  const hasPermission = (menuItem: string) => {
    const allowedRoles =
      menuPermissions[menuItem as keyof typeof menuPermissions] || [];
    return allowedRoles.includes(userRole);
  };

  // Define menu items based on user permissions
  const getMenuItems = () => {
    const allMenuItems = [
      {
        key: "1",
        label: <NavLink to="/dashboard">Dashboard</NavLink>,
        title: "Dashboard",
        icon: <HomeOutlined />,
        permission: "dashboard",
      },
      {
        key: "2",
        label: <NavLink to="/users">User</NavLink>,
        title: "User Management",
        icon: <TeamOutlined />,
        permission: "users",
      },
      {
        key: "3",
        label: <NavLink to="/files">File</NavLink>,
        title: "File Management",
        icon: <FileTextFilled />,
        permission: "files",
      },
      ...(user.branch?.branchName === "Seremban" || user.role === "admin"
        ? [
            {
              key: "4",
              label: "Staff",
              icon: <UserOutlined />,
              permission: "staff",
              children: [
                {
                  key: "4.1",
                  label: <NavLink to="/leaves">Leave</NavLink>,
                  title: "Leave",
                  icon: <CalendarOutlined />,
                },
                {
                  key: "4.2",
                  label: <NavLink to="/claims">Claim</NavLink>,
                  title: "Claim",
                  icon: <DollarOutlined />,
                },
                {
                  key: "4.3",
                  label: <NavLink to="/payslips">Payslip</NavLink>,
                  title: "Payslip",
                  icon: <FileTextFilled />,
                },
              ],
            },
          ]
        : []),
    ];

    // Filter menu items based on user permissions
    return allMenuItems
      .filter((item) => hasPermission(item.permission))
      .map(({ permission: _permission, ...item }) => item); // Remove permission from final menu items
  };

  return (
    <Menu
      mode="inline"
      selectedKeys={[getSelectedKey(location.pathname)]}
      items={getMenuItems() as MenuItem[]}
    />
  );
};

export default SidebarMenu;
